import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NaturalLanguageFiller } from '@/components/NaturalLanguageFiller';
import { AccountCombobox } from '@/components/AccountCombobox';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import {
  ACQUISITION_CHANNELS,
  CURRENCIES,
  DEAL_TYPES,
  PAYMENT_TERMS,
} from '@/lib/validation';

interface Template { id: string; name: string; scope: string; }
interface Campaign { id: string; name: string; }

const FORECASTS = ['PIPELINE', 'UPSIDE', 'BEST_CASE', 'COMMIT'] as const;
const PRIORITIES = ['P1', 'P2', 'P3'] as const;

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  preselectedAccountId?: string;
  preselectedCampaignId?: string;
}

export function CreateOpportunityModal({
  open,
  onClose,
  onCreated,
  preselectedAccountId,
  preselectedCampaignId,
}: Props) {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  // Identity
  const [accountId, setAccountId] = useState(preselectedAccountId ?? '');
  const [campaignId, setCampaignId] = useState(preselectedCampaignId ?? '');
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState('');

  // Financial
  const [dealValue, setDealValue] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [dealType, setDealType] = useState('NEW_BUSINESS');
  const [recurringRevenue, setRecurringRevenue] = useState('');
  const [oneTimeRevenue, setOneTimeRevenue] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');

  // Forecast + planning
  const [forecast, setForecast] = useState('PIPELINE');
  const [probabilityPercent, setProbabilityPercent] = useState('');
  const [expectedClose, setExpectedClose] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [nextStepDate, setNextStepDate] = useState('');
  const [priority, setPriority] = useState('P3');
  const [acquisitionChannel, setAcquisitionChannel] = useState('');

  // Qualification (MEDDIC-ish)
  const [decisionCriteria, setDecisionCriteria] = useState('');
  const [painPoints, setPainPoints] = useState('');
  const [compellingEvent, setCompellingEvent] = useState('');
  const [compellingEventDate, setCompellingEventDate] = useState('');

  // Classification
  const [tagsRaw, setTagsRaw] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (preselectedAccountId) setAccountId(preselectedAccountId);
    if (preselectedCampaignId) setCampaignId(preselectedCampaignId);
    Promise.all([
      apiClient.get<Template[]>('/process-templates'),
      apiClient.get<Campaign[]>('/campaigns'),
    ])
      .then(([tmplRes, campRes]) => {
        setTemplates(tmplRes.data);
        setCampaigns(campRes.data);
      })
      .catch(() => {});
  }, [open, preselectedAccountId, preselectedCampaignId]);

  // When account is picked, try to inherit its currency.
  useEffect(() => {
    if (!accountId) return;
    apiClient
      .get<{ currencyPreference?: string | null }>(`/accounts/${accountId}`)
      .then(({ data }) => {
        if (data.currencyPreference && currency === 'USD') {
          setCurrency(data.currencyPreference);
        }
      })
      .catch(() => {});
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  const probabilityErr = useMemo(() => {
    if (!probabilityPercent) return null;
    const n = Number(probabilityPercent);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? null : 'validation.percent.range';
  }, [probabilityPercent]);
  const discountErr = useMemo(() => {
    if (!discountPercent) return null;
    const n = Number(discountPercent);
    return Number.isFinite(n) && n >= 0 && n <= 100 ? null : 'validation.percent.range';
  }, [discountPercent]);

  if (!open) return null;

  function reset() {
    setAccountId(preselectedAccountId ?? '');
    setCampaignId(preselectedCampaignId ?? '');
    setName(''); setTemplateId('');
    setDealValue(''); setCurrency('USD'); setDealType('NEW_BUSINESS');
    setRecurringRevenue(''); setOneTimeRevenue(''); setPaymentTerms(''); setDiscountPercent('');
    setForecast('PIPELINE'); setProbabilityPercent(''); setExpectedClose('');
    setNextStep(''); setNextStepDate(''); setPriority('P3'); setAcquisitionChannel('');
    setDecisionCriteria(''); setPainPoints(''); setCompellingEvent(''); setCompellingEventDate('');
    setTagsRaw(''); setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accountId || !name.trim()) return;
    if (probabilityErr || discountErr) return;
    setSubmitting(true);
    setError(null);
    try {
      const tags = tagsRaw.split(',').map((s) => s.trim()).filter(Boolean);
      await apiClient.post('/opportunities', {
        accountId,
        name: name.trim(),
        templateId: templateId || undefined,
        campaignId: campaignId || undefined,
        estimatedDealValue: dealValue ? parseFloat(dealValue) : 0,
        currency,
        dealType,
        expectedRevenueRecurring: recurringRevenue ? parseFloat(recurringRevenue) : undefined,
        expectedRevenueOnetime: oneTimeRevenue ? parseFloat(oneTimeRevenue) : undefined,
        paymentTerms: paymentTerms || undefined,
        discountPercent: discountPercent ? parseFloat(discountPercent) : undefined,
        forecastClassification: forecast,
        probabilityPercent: probabilityPercent ? Number(probabilityPercent) : undefined,
        expectedCloseDate: expectedClose || undefined,
        nextStep: nextStep.trim() || undefined,
        nextStepDate: nextStepDate || undefined,
        priority,
        acquisitionChannel: acquisitionChannel || undefined,
        decisionCriteria: decisionCriteria.trim() || undefined,
        painPoints: painPoints.trim() || undefined,
        compellingEvent: compellingEvent.trim() || undefined,
        compellingEventDate: compellingEventDate || undefined,
        competitorNames: [],
        tags: tags.length > 0 ? tags : undefined,
      });
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      const msg = detail ?? (err instanceof Error ? err.message : t('createOpportunity.error.generic'));
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !submitting && accountId && name.trim() && !probabilityErr && !discountErr;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('createOpportunity.title')}</h3>
          <NaturalLanguageFiller
            fieldSchema={[
              { name: 'name', label: 'Opportunity name', type: 'text', required: true },
              { name: 'dealValue', label: 'Estimated value', type: 'number' },
              { name: 'currency', label: 'Currency (3-letter ISO)', type: 'text' },
              { name: 'forecast', label: 'Forecast classification', type: 'text' },
              { name: 'expectedClose', label: 'Expected close date', type: 'date' },
              { name: 'nextStep', label: 'Next step', type: 'text' },
              { name: 'painPoints', label: 'Pain points', type: 'text' },
              { name: 'compellingEvent', label: 'Compelling event', type: 'text' },
            ]}
            currentValues={{ name, dealValue, currency, forecast, expectedClose, nextStep, painPoints, compellingEvent }}
            onApply={(v) => {
              if (v.name !== undefined) setName(v.name);
              if (v.dealValue !== undefined) setDealValue(v.dealValue);
              if (v.currency !== undefined) setCurrency(v.currency.toUpperCase());
              if (v.forecast !== undefined) setForecast(v.forecast.toUpperCase());
              if (v.expectedClose !== undefined) setExpectedClose(v.expectedClose);
              if (v.nextStep !== undefined) setNextStep(v.nextStep);
              if (v.painPoints !== undefined) setPainPoints(v.painPoints);
              if (v.compellingEvent !== undefined) setCompellingEvent(v.compellingEvent);
            }}
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Identity */}
          <FormSection title={t('createOpportunity.section.identity')} defaultOpen>
            <FormField label={t('createOpportunity.field.account')} required>
              <AccountCombobox
                value={accountId || null}
                onChange={(id) => setAccountId(id ?? '')}
                disabled={Boolean(preselectedAccountId)}
              />
            </FormField>
            <FormField label={t('createOpportunity.field.name')} required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('createOpportunity.placeholder.name')}
                required
              />
            </FormField>
            <FormGrid>
              <FormField label={t('createOpportunity.field.campaign')}>
                <select
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                  disabled={Boolean(preselectedCampaignId)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm disabled:opacity-60"
                >
                  <option value="">{t('createOpportunity.option.noCampaign')}</option>
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t('createOpportunity.field.template')}>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{t('createOpportunity.option.defaultTemplate')}</option>
                  {templates.map((tmpl) => (
                    <option key={tmpl.id} value={tmpl.id}>
                      {tmpl.name} ({tmpl.scope})
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
          </FormSection>

          {/* Financial */}
          <FormSection title={t('createOpportunity.section.financial')} defaultOpen>
            <FormGrid cols={3}>
              <FormField label={t('createOpportunity.field.dealValue')}>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={dealValue}
                  onChange={(e) => setDealValue(e.target.value)}
                  placeholder="50000"
                />
              </FormField>
              <FormField label={t('createOpportunity.field.currency')}>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t('createOpportunity.field.dealType')}>
                <select
                  value={dealType}
                  onChange={(e) => setDealType(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {DEAL_TYPES.map((d) => (
                    <option key={d} value={d}>
                      {t(`reference.dealType.${d}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
          </FormSection>

          {/* Forecast + planning */}
          <FormSection title={t('createOpportunity.section.forecast')} defaultOpen>
            <FormGrid cols={3}>
              <FormField label={t('createOpportunity.field.forecast')}>
                <select
                  value={forecast}
                  onChange={(e) => setForecast(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {FORECASTS.map((f) => (
                    <option key={f} value={f}>
                      {t(`reference.forecast.${f}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label={t('createOpportunity.field.probability')}
                hint={t('createOpportunity.hint.probability')}
                error={probabilityErr ? t(probabilityErr) : null}
              >
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={probabilityPercent}
                  onChange={(e) => setProbabilityPercent(e.target.value)}
                  placeholder="60"
                />
              </FormField>
              <FormField label={t('createOpportunity.field.expectedClose')}>
                <Input
                  type="date"
                  value={expectedClose}
                  onChange={(e) => setExpectedClose(e.target.value)}
                />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('createOpportunity.field.nextStep')}>
                <Input
                  value={nextStep}
                  onChange={(e) => setNextStep(e.target.value)}
                  placeholder={t('createOpportunity.placeholder.nextStep')}
                />
              </FormField>
              <FormField label={t('createOpportunity.field.nextStepDate')}>
                <Input
                  type="date"
                  value={nextStepDate}
                  onChange={(e) => setNextStepDate(e.target.value)}
                />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('createOpportunity.field.priority')}>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t('createOpportunity.field.acquisitionChannel')}>
                <select
                  value={acquisitionChannel}
                  onChange={(e) => setAcquisitionChannel(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {ACQUISITION_CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {t(`reference.acquisitionChannel.${c}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
          </FormSection>

          {/* Qualification (collapsed by default — most reps fill this later) */}
          <FormSection title={t('createOpportunity.section.qualification')} defaultOpen={false}>
            <FormField
              label={t('createOpportunity.field.decisionCriteria')}
              hint={t('createOpportunity.hint.decisionCriteria')}
            >
              <textarea
                value={decisionCriteria}
                onChange={(e) => setDecisionCriteria(e.target.value)}
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </FormField>
            <FormField
              label={t('createOpportunity.field.painPoints')}
              hint={t('createOpportunity.hint.painPoints')}
            >
              <textarea
                value={painPoints}
                onChange={(e) => setPainPoints(e.target.value)}
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </FormField>
            <FormGrid>
              <FormField
                label={t('createOpportunity.field.compellingEvent')}
                hint={t('createOpportunity.hint.compellingEvent')}
              >
                <Input
                  value={compellingEvent}
                  onChange={(e) => setCompellingEvent(e.target.value)}
                  placeholder={t('createOpportunity.placeholder.compellingEvent')}
                />
              </FormField>
              <FormField label={t('createOpportunity.field.compellingEventDate')}>
                <Input
                  type="date"
                  value={compellingEventDate}
                  onChange={(e) => setCompellingEventDate(e.target.value)}
                />
              </FormField>
            </FormGrid>
          </FormSection>

          {/* More: revenue split + payment + tags */}
          <FormSection title={t('createOpportunity.section.more')} defaultOpen={false}>
            <FormGrid>
              <FormField label={t('createOpportunity.field.recurringRevenue')}>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={recurringRevenue}
                  onChange={(e) => setRecurringRevenue(e.target.value)}
                />
              </FormField>
              <FormField label={t('createOpportunity.field.oneTimeRevenue')}>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={oneTimeRevenue}
                  onChange={(e) => setOneTimeRevenue(e.target.value)}
                />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('createOpportunity.field.paymentTerms')}>
                <select
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {PAYMENT_TERMS.map((p) => (
                    <option key={p} value={p}>
                      {t(`reference.paymentTerms.${p}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label={t('createOpportunity.field.discountPercent')}
                error={discountErr ? t(discountErr) : null}
              >
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(e.target.value)}
                />
              </FormField>
            </FormGrid>
            <FormField label={t('createOpportunity.field.tags')} hint={t('createOpportunity.hint.tags')}>
              <Input value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} />
            </FormField>
          </FormSection>

          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {submitting ? t('createOpportunity.submit.creating') : t('createOpportunity.submit.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
